import type {Currency} from "#pricing/types/currency"
import type {PriceMap} from "#pricing/types/pricing-types"

export class PricingContext {
    private priceMap: PriceMap
    private currencies: Currency[]
    private readonly currency: Currency
    private readonly priceIncludesVat: boolean

    constructor(priceMap: PriceMap, currency: Currency) {
        this.priceMap = priceMap
        this.currencies = PricingContext.computeAvailableCurrencies(priceMap)
        if (!this.currencies.includes(currency)) {
            throw new Error(`Currency ${currency} is not available in this context. Available: ${this.currencies.join(", ")}`)
        }
        this.currency = currency
        this.priceIncludesVat = PricingContext.computePriceIncludesVat(priceMap, currency)
    }

    /** Currencies available across *every* item in this context (intersection). */
    getCurrencies(): Currency[] {
        return this.currencies
    }

    getPrice(uniqueId: string): number {
        return this.getPriceEntry(uniqueId).price
    }

    /** Whether prices in this context (for its resolved currency) are VAT-inclusive. Uniform across all items by construction — verified at construction time. */
    getPriceIncludesVat(): boolean {
        return this.priceIncludesVat
    }

    private getPriceEntry(uniqueId: string) {
        const pricesForItem = this.priceMap.get(uniqueId)
        if (pricesForItem === undefined) throw new Error("Price not found")
        const entry = pricesForItem.get(this.currency)
        if (entry === undefined) throw new Error(`Price not found for currency ${this.currency}`)
        return entry
    }

    getCurrency(): Currency {
        return this.currency
    }

    /** Currencies available across all items in a price map (intersection). Useful before constructing a context. */
    static getAvailableCurrencies(priceMap: PriceMap): Currency[] {
        return PricingContext.computeAvailableCurrencies(priceMap)
    }

    private static computeAvailableCurrencies(priceMap: PriceMap): Currency[] {
        const iterator = priceMap.values()
        const first = iterator.next()
        if (first.done) return []
        let intersection = new Set(first.value.keys())
        for (const perItem of iterator) {
            intersection = new Set([...intersection].filter((c) => perItem.has(c)))
            if (intersection.size === 0) break
        }
        return [...intersection]
    }

    /** VAT-inclusiveness is a property of a (catalog, currency), so it must be uniform across all items for a given currency. Verified here so the rest of the pricing pipeline can treat it as a single per-result value. */
    private static computePriceIncludesVat(priceMap: PriceMap, currency: Currency): boolean {
        let result: boolean | undefined
        for (const perItem of priceMap.values()) {
            const entry = perItem.get(currency)
            if (entry === undefined) continue
            if (result === undefined) {
                result = entry.priceIncludesVat
            } else if (result !== entry.priceIncludesVat) {
                throw new Error(`Mixed VAT treatment for currency ${currency} is not supported`)
            }
        }
        if (result === undefined) throw new Error(`Price not found for currency ${currency}`)
        return result
    }
}
